import { z } from 'zod';
import { t } from '@/i18n';
import { SeasonType } from '@/model';
import { MIN_GROUPS, DEFAULT_STANDINGS_COLUMNS, STANDINGS_COLUMN_KEYS } from './constants';

const requiredText = t('utils.schema.required').toString();

const i18n = (key: string) => t(`pages.application_season.components.${key}`).toString();

/**
 * Cutoffs de qualifier zones — quantos times classificam direto, vão para
 * repescagem e são rebaixados em uma fase do tipo LEAGUE ou GROUP.
 * `KNOCKOUT` não usa (a UI nem exibe).
 */
export const qualifierZonesSchema = z.object({
  direct_qualifiers_count: z.coerce.number().int().min(0).default(0),
  playoff_qualifiers_count: z.coerce.number().int().min(0).default(0),
  secondary_qualifiers_count: z.coerce.number().int().min(0).default(0),
  relegated_count: z.coerce.number().int().min(0).default(0),
  // Nomes customizados das zonas. Vazio = usa o label default do i18n.
  direct_qualifiers_label: z.string().max(60).default(''),
  playoff_qualifiers_label: z.string().max(60).default(''),
  secondary_qualifiers_label: z.string().max(60).default(''),
  relegated_qualifiers_label: z.string().max(60).default(''),
});

export const groupStageItemSchema = z.object({
  // Presente quando o stage veio do banco (modo Edit); ausente em stages novas.
  uuid: z.string().optional(),
  group_name: z.string().default(''),
  // UUIDs dos times (Club) alocados neste grupo. Em HYBRID, definido na aba Times.
  team_uuids: z.array(z.string()).default([]),
  direct_qualifiers_count: z.coerce.number().int().min(0).default(0),
  playoff_qualifiers_count: z.coerce.number().int().min(0).default(0),
  secondary_qualifiers_count: z.coerce.number().int().min(0).default(0),
  relegated_count: z.coerce.number().int().min(0).default(0),
  direct_qualifiers_label: z.string().max(60).default(''),
  playoff_qualifiers_label: z.string().max(60).default(''),
  secondary_qualifiers_label: z.string().max(60).default(''),
  relegated_qualifiers_label: z.string().max(60).default(''),
});

export const knockoutStageItemSchema = z.object({
  uuid: z.string().optional(),
  name: z.string().default(''),
});

export const seasonTeamItemSchema = z.object({
  // Presente quando o SeasonTeam veio do banco (Edit).
  uuid: z.string().optional(),
  team_uuid: z.string(),
});

export const seasonFormSchema = z
  .object({
    step: z.number().default(1),

    // usado no EDIT (PUT)
    season_uuid: z.string().optional(),

    championship_uuid: z.string().optional(),
    season_year: z.string().optional(),
    season_type: z.nativeEnum(SeasonType).optional(),

    is_filtered: z.boolean().default(false),
    is_active: z.boolean().default(true),

    // só usado quando season_type === HYBRID
    groups_count: z.number().default(MIN_GROUPS),

    // fases de grupo (HYBRID) — nome do grupo é editável
    group_stages: z.array(groupStageItemSchema).default([]),

    // fases de mata-mata (KNOCKOUT e HYBRID)
    knockout_stages: z.array(knockoutStageItemSchema).default([]),

    // times participantes da temporada (Step 3)
    season_teams: z.array(seasonTeamItemSchema).default([]),

    // Configuração de qualifier zones da fase única (apenas LEAGUE).
    // Em HYBRID os cutoffs ficam por grupo, em group_stages[i].
    // Em KNOCKOUT é ignorado.
    league_stage_config: qualifierZonesSchema.default({
      direct_qualifiers_count: 0,
      playoff_qualifiers_count: 0,
      secondary_qualifiers_count: 0,
      relegated_count: 0,
      direct_qualifiers_label: '',
      playoff_qualifiers_label: '',
      secondary_qualifiers_label: '',
      relegated_qualifiers_label: '',
    }),

    // Visibilidade das colunas da tabela de classificação (nível temporada).
    // Default: todas visíveis.
    standings_columns: z.record(z.boolean()).default(DEFAULT_STANDINGS_COLUMNS),

    // uuid da fase LEAGUE existente — usado no Edit pra emitir PUT quando os
    // cutoffs forem alterados. Ausente em Create.
    league_stage_uuid: z.string().optional(),
  })
  .superRefine((values, ctx) => {
    const validateStep1 = () => {
      if (!values.championship_uuid) {
        ctx.addIssue({
          code: z.ZodIssueCode.custom,
          path: ['championship_uuid'],
          message: requiredText,
        });
      }

      if (!values.season_year) {
        ctx.addIssue({
          code: z.ZodIssueCode.custom,
          path: ['season_year'],
          message: requiredText,
        });
      }

      if (!values.season_type) {
        ctx.addIssue({
          code: z.ZodIssueCode.custom,
          path: ['season_type'],
          message: requiredText,
        });
      }

      if (values.season_type === SeasonType.HYBRID && (!values.groups_count || values.groups_count < MIN_GROUPS)) {
        ctx.addIssue({
          code: z.ZodIssueCode.custom,
          path: ['groups_count'],
          message: t('pages.application_season.components.season_step1.schema.groups_min', {
            min: MIN_GROUPS,
          }).toString(),
        });
      }

      // Pelo menos uma coluna da tabela de classificação precisa ficar visível.
      const visibleColumns = STANDINGS_COLUMN_KEYS.filter(
        key => values.standings_columns?.[key] !== false,
      ).length;
      if (visibleColumns === 0) {
        ctx.addIssue({
          code: z.ZodIssueCode.custom,
          path: ['standings_columns'],
          message: i18n('season_step1.standings_columns.min_one'),
        });
      }
    };

    const qualifiersOverflowMessage = i18n('season_step2.schema.qualifiers_overflow');

    const validateStep2 = () => {
      const requireKnockout = (path: string) => {
        if (values.knockout_stages.length === 0) {
          ctx.addIssue({
            code: z.ZodIssueCode.custom,
            path: ['knockout_stages'],
            message: i18n(`season_step2.schema.${path}`),
          });
        }
        values.knockout_stages.forEach((stage, index) => {
          if (!stage.name.trim()) {
            ctx.addIssue({
              code: z.ZodIssueCode.custom,
              path: ['knockout_stages', index, 'name'],
              message: requiredText,
            });
          }
        });
      };

      if (values.season_type === SeasonType.LEAGUE) {
        const sum =
          (values.league_stage_config?.direct_qualifiers_count ?? 0) +
          (values.league_stage_config?.playoff_qualifiers_count ?? 0) +
          (values.league_stage_config?.secondary_qualifiers_count ?? 0) +
          (values.league_stage_config?.relegated_count ?? 0);
        const total = values.season_teams.length;
        // Em Create os times são escolhidos no Step 3 — pula o overflow enquanto
        // não houver times; a validação roda de novo no submit final.
        if (total > 0 && sum > total) {
          ctx.addIssue({
            code: z.ZodIssueCode.custom,
            path: ['league_stage_config', 'direct_qualifiers_count'],
            message: qualifiersOverflowMessage,
          });
        }
      }

      if (values.season_type === SeasonType.KNOCKOUT) {
        requireKnockout('stages_required');
      }

      if (values.season_type === SeasonType.HYBRID) {
        if (values.group_stages.length === 0) {
          ctx.addIssue({
            code: z.ZodIssueCode.custom,
            path: ['group_stages'],
            message: i18n('season_step2.schema.groups_required'),
          });
        }
        values.group_stages.forEach((stage, index) => {
          if (!stage.group_name.trim()) {
            ctx.addIssue({
              code: z.ZodIssueCode.custom,
              path: ['group_stages', index, 'group_name'],
              message: requiredText,
            });
          }
          const sum =
            (stage.direct_qualifiers_count ?? 0) +
            (stage.playoff_qualifiers_count ?? 0) +
            (stage.secondary_qualifiers_count ?? 0) +
            (stage.relegated_count ?? 0);
          // Em Create a alocação de times por grupo é feita no Step 3 — pula
          // o overflow enquanto o grupo estiver vazio; revalidado no submit.
          if (stage.team_uuids.length > 0 && sum > stage.team_uuids.length) {
            ctx.addIssue({
              code: z.ZodIssueCode.custom,
              path: ['group_stages', index, 'direct_qualifiers_count'],
              message: qualifiersOverflowMessage,
            });
          }
        });
        requireKnockout('elimination_required');
      }
    };

    const validateStep3 = () => {
      // Step 3 (Times): por enquanto sem regras estritas — o usuário decide o
      // tamanho da seleção e a alocação por grupo. Espaço reservado caso
      // queiramos exigir mín/máx no futuro.
    };

    switch (values.step) {
      case 1:
        validateStep1();
        break;
      case 2:
        validateStep2();
        break;
      case 3:
        validateStep3();
        break;
      default:
        validateStep1();
        validateStep2();
        validateStep3();
        break;
    }
  });

export type SeasonFormData = z.infer<typeof seasonFormSchema>;
export type GroupStageItem = z.infer<typeof groupStageItemSchema>;
export type KnockoutStageItem = z.infer<typeof knockoutStageItemSchema>;
export type SeasonTeamItem = z.infer<typeof seasonTeamItemSchema>;

export type SeasonWizardMode = 'create' | 'edit';
